<script lang="ts">
    import { type EventPlan, FocusTypes, ProgramType, type RacePlan, type RaceRecords, type Signature, programTypeIcon, shortProgramTypeTitle, xfaiProgramTypes, improvementRateDescriptions, specificityValues } from "@/Common/constants";
    import type { MedianAndWeatherStores, MyFitnessStores } from "@/MyFitness/stores";
    import { StrToProgramType, type XSSDetails, arrayFindMax, arrayFindMin, calcTsRating, createEventPlan, createRacePlan, debouncer, getForecastResultsForChart, getLHPGradient, getLTP, roundTo1Decimal, durationToHoursMinutes, round, getDisplayWeight, xssDetailsToSignature } from "@/Util/utils";
    import { LocalDate } from "@/time";
    import { addDays, addSeconds, differenceInDays, endOfDay, endOfWeek, format, fromUnixTime, isSameDay, startOfDay, startOfWeek } from "date-fns";
    import { getContext, onMount } from "svelte";
    import { getPredictiveState } from "../PredictiveMode/predictive";
    import PlanStatusBanner from "./PlanStatusBanner.svelte";
    import PredictiveModeBadge from "../PredictiveMode/PredictiveModeBadge.svelte";
    import BreakthroughProposal from "./BreakthroughProposal.svelte";
    import {
        PlanFitSigTLTable,
        XfaiXss,
        EventReadiness,
        PlanMeter,
        ProgramAchievabilityMessage,
        ProgramNameIcon,
        FocusPowerIncrease,
    } from "./ProgramElements"
    import { processRecordsForChart } from '@/Util/XertAm5';
    import Am5Loader from "../Util/Am5Loader.svelte";
    import { AnalyticsController, type SummaryData } from "@/Activities/ActivityAnalytics";
    import { getTLFitSigFromHistory } from "@/Util/api";
    import TrainingStatus from "../MyFitness/TrainingStatus.svelte";
    import { createUserFocusPowers } from "./modules/UserFocusPowers";
    import CurrentTpDateSlider from "./ProgramElements/CurrentTPDateSlider.svelte";
    import { createProgramAchievability } from "./modules/ProgramAchievability";
    import ResponsiveHalfView from "./ResponsiveHalfView.svelte";
    import { Button } from "$lib/components/ui/button/index.js";
    import AdaptForecastNeeded from "../MyFitness/Calendar/AdaptForecastNeeded.svelte";
    import { Accordion, Skeleton } from "@/Common";
    import RaceMapModal from "./RaceMapModal.svelte";
    import ProgressionChart from "../MyFitness/TrainingForecast/ProgressionChart.svelte";
    import DottedLineMarker from "./ProgramElements/SetupFormInputs/SVGMarkers/DottedLineMarker.svelte";
    import CurrentMarker from "./ProgramElements/SetupFormInputs/SVGMarkers/CurrentMarker.svelte";
    import TargetMarker from "./ProgramElements/SetupFormInputs/SVGMarkers/TargetMarker.svelte";
    import { fade } from "svelte/transition";
    import PrePlanBanner from "./PrePlanBanner.svelte";

    // Adapt Forecast saves for the signed-in athlete, so it is offered only once this page is known to be their own (not a coach's view
    // of another athlete, whose plan the dialog would read and save under the coach's account).
    let ownPlan = false;
    onMount(async () => { ownPlan = !!(await getPredictiveState()); });

    export let editPlanCallback: () => void
    export let hidden: boolean = false

    //@ts-expect-error
    const google = window.google
    export let isGoogleMapsLoaded = !!google

    // This variable is for bind-only
    export let raceRecords: RaceRecords

    const { trainingAdvice, trainingPlan, userParams, cal_settings } = getContext<MyFitnessStores>("myFitness");
    const { trainingMedians } = getContext<MedianAndWeatherStores>("medianAndWeather")

    let adaptForecastModal = null
    let showMapModal = () => {}


    // Returning to the page overnight without refreshes causes crashes... this might fix it
    let dateNow = new Date()

    let xpmcDayTypeLabel = 'Projected'
    let forecastedDayTypeLabel = 'Required'
    let programType: ProgramType = ProgramType.Continuous
    let focus: string = null
    let specificity: number = null
    let planDate: Date = null
    let planCreationDate: Date = null
    let startingSignature: XSSDetails = null
    let currentSignature: XSSDetails = null
    let xpmcProjSignature: XSSDetails = null
    let forecastSignature: XSSDetails = null
    let finalSignature: XSSDetails = null
    let startingTLs: XSSDetails = null
    let currentTLs: XSSDetails = null
    let xpmcProjTLs: XSSDetails = null
    let forecastTLs: XSSDetails = null
    let finalTLs: XSSDetails = null
    let xpmcProjFocusPower: number = null
    let forecastFocusPower: number = null
    let showExpectedFor_dayIndex = 0
    let showExpectedFor_dayIndexMax = 1
    let xpmcProjectedData: {date: Date, tl, signature, fp: number}[] = []
    let sliderStartDateIdx = 0
    let planStartIdx = 0
    let xpmcProj_isReady = false
    let tlMax: XSSDetails = null
    let tlMin: XSSDetails = null
    let sigMax: XSSDetails = null
    let sigMin: XSSDetails = null
    let fpLimits: {min: number, max: number} = null
    let willShowCurrentValues = false
    let xpmcProjDate: Date = new Date()
    let curSliderDate: Date = new Date()

    // For XFAI Programs
    // let forecastResults = null
    let chartData = null
    let lastInputs = null

    // For Race Programs
    let racePlan: RacePlan = null
    let mpaData: any = null
    let raceAnalytics: AnalyticsController = null
    let summary: Partial<SummaryData> = {}
    let chartAvgDataOverSeconds = 5
    let debounceRefreshChartMpaData = debouncer(500)
    export let logoURL = null
    let sfd: number = null
    let sp: number = null

    // For Event Programs
    let eventPlan: EventPlan = null
    let eventXSS: XSSDetails = null

    // For Goal Programs
    let startingFocusPower: number = null
    const {currentFocusPower, focusPowerDuration, updateFocusPower} = createUserFocusPowers(trainingAdvice)
    let finalFocusPower: number = null
    let focusDuration: string = null // For display

    // For 120 Day Programs
    let thisWeekStats: {weekly_hours: number, phase: string} = null
    let weeklyStats: Record<string, typeof thisWeekStats> = {}

    // For xata programs
    let irText: string = null
    // For continuous programs
    /**
     * `Continuous -> Weekly RR`
     * `Challenge -> Daily RR`
     * */
    let rampRateStats: number[] = null
    const programAchievability = createProgramAchievability(null) // Program Type doesn't matter
    const {overallAchievability, statusMessage} = programAchievability

    const resetData = () => {
        programType = ProgramType.Continuous
        focus = null
        sfd = null
        sp = null
        specificity = null
        planDate = null
        startingSignature = null
        currentSignature = null
        xpmcProjSignature = null
        finalSignature = null
        startingTLs = null
        finalTLs = null
        currentTLs = null
        xpmcProjTLs = null
        startingFocusPower = null
        xpmcProjFocusPower = null
        finalFocusPower = null
        focusDuration = null
        irText = null
        eventPlan = null
        racePlan = null
        mpaData = null
        raceAnalytics = null
        summary = {}
        showExpectedFor_dayIndex = 0
        showExpectedFor_dayIndexMax = 1
        xpmcProjectedData = []
        xpmcProj_isReady = false
        sliderStartDateIdx = 0
        planStartIdx = 0
        tlMax = null
        tlMin = null
        sigMax = null
        sigMin = null
        eventXSS = null
        logoURL = null
        rampRateStats = null
        weeklyStats = {}
        thisWeekStats = null
        lastInputs = null
        chartData = null
        forecastSignature = null
        forecastTLs = null
        forecastFocusPower = null
        fpLimits = null
        dateNow = new Date()
        willShowCurrentValues = false
        xpmcProjDate = new Date()
        curSliderDate = new Date()
    }

    const onHistoryLoaded = (res) => {

        if (res.race_records) {
            raceRecords = res.race_records
            refreshChartMpaData()
        }

        const forecastDays: any[] = $trainingPlan?.forecastResults?.days ?? []

        Object.entries(res.days).forEach(([dateString, data]: [string, any]) => {
            const date = LocalDate.toDate(dateString)
            xpmcProjectedData.push({
                date,
                tl: data.tl,
                signature: data.signature,
                fp: data.fp,
            })
        })

        tlMax = {
            xlss: Math.max(arrayFindMax(xpmcProjectedData, ['tl', 'ftp']), arrayFindMax(forecastDays, ['tls', 'l'])),
            xhss: Math.max(arrayFindMax(xpmcProjectedData, ['tl', 'hie']), arrayFindMax(forecastDays, ['tls', 'h'])),
            xpss: Math.max(arrayFindMax(xpmcProjectedData, ['tl', 'pp']), arrayFindMax(forecastDays, ['tls', 'p'])),
            xss: 0,
        }
        tlMax.xss = tlMax.xlss + tlMax.xhss + tlMax.xpss
        tlMin = {
            xlss: Math.min(arrayFindMin(xpmcProjectedData, ['tl', 'ftp']), arrayFindMin(forecastDays, ['tls', 'l']) ?? Infinity),
            xhss: Math.min(arrayFindMin(xpmcProjectedData, ['tl', 'hie']), arrayFindMin(forecastDays, ['tls', 'h']) ?? Infinity),
            xpss: Math.min(arrayFindMin(xpmcProjectedData, ['tl', 'pp']), arrayFindMin(forecastDays, ['tls', 'p']) ?? Infinity),
            xss: 0,
        }
        tlMin.xss = tlMin.xlss + tlMin.xhss + tlMin.xpss
        sigMax = {
            xlss: Math.max(arrayFindMax(xpmcProjectedData, ['signature', 'ftp']), arrayFindMax(forecastDays, ['ftp'])),
            xhss: Math.max(arrayFindMax(xpmcProjectedData, ['signature', 'atc']), arrayFindMax(forecastDays, ['hie'])) / 1000,
            xpss: Math.max(arrayFindMax(xpmcProjectedData, ['signature', 'pp']), arrayFindMax(forecastDays, ['pp'])),
            xss: 0,
        }
        sigMin = {
            xlss: Math.min(arrayFindMin(xpmcProjectedData, ['signature', 'ftp']), arrayFindMin(forecastDays, ['ftp']) ?? Infinity),
            xhss: Math.min(arrayFindMin(xpmcProjectedData, ['signature', 'atc']), arrayFindMin(forecastDays, ['hie']) ?? Infinity) / 1000,
            xpss: Math.min(arrayFindMin(xpmcProjectedData, ['signature', 'pp']), arrayFindMin(forecastDays, ['pp']) ?? Infinity),
            xss: 0,
        }
        fpLimits = {
            min: Math.min(arrayFindMin(xpmcProjectedData, ['fp']), arrayFindMin(forecastDays, ['fp']) ?? Infinity),
            max: Math.max(arrayFindMax(xpmcProjectedData, ['fp']), arrayFindMax(forecastDays, ['fp'])),
        }

        const isXFAI = $trainingPlan?.hasForecast
        if (!isXFAI) {
            const dayStart = xpmcProjectedData.find(data => isSameDay(data.date, planCreationDate)) ?? xpmcProjectedData.at(0)
            const dayFinal = xpmcProjectedData.at(-1)
            startingTLs = {
                xss: dayStart.tl.ftp + dayStart.tl.hie + dayStart.tl.pp,
                xlss: dayStart.tl.ftp,
                xpss: dayStart.tl.pp,
                xhss: dayStart.tl.hie,
            }
            finalTLs = {
                xss: dayFinal.tl.ftp + dayFinal.tl.hie + dayFinal.tl.pp,
                xlss: dayFinal.tl.ftp,
                xpss: dayFinal.tl.pp,
                xhss: dayFinal.tl.hie,
            }
            startingSignature = {
                xss: 0,
                xlss: dayStart.signature.ftp,
                xpss: dayStart.signature.pp,
                xhss: dayStart.signature.atc / 1000,
            }
            finalSignature = {
                xss: 0,
                xlss: dayFinal.signature.ftp,
                xpss: dayFinal.signature.pp,
                xhss: dayFinal.signature.atc / 1000,
            }
            startingFocusPower = dayStart.fp
            finalFocusPower = dayFinal.fp
        }

        rampRateStats = res.ramprate_stats ?? null

        weeklyStats = res.weekly_stats ?? null

        xpmcProj_isReady = true

        updateExpectedDay() // Uses showExpectedFor_dayIndex
    }

    const refreshChartMpaData = async () => {
        const currentSignatureReal: Signature = $trainingAdvice.signature
        const params = $userParams

        // Use final projected signature for MPA data, if available.
        const ftp =  finalSignature.xlss ?? currentSignatureReal.ftp
        const atc = finalSignature.xhss ? (finalSignature.xhss*1000.0) : currentSignatureReal.atc
        const pp = finalSignature.xpss ?? currentSignatureReal.pp
        const ltp = getLTP(ftp, atc)

        mpaData = await processRecordsForChart(
            raceRecords.race_data,
            {
                ftp,
                atc,
                pp,
                ltp,
                carb_bias: params.carb_bias,
            }, 
            params.metric,
            chartAvgDataOverSeconds
        )
    }

    const onPlanUpdated = async (plan, advice, params) => {
        resetData()
        programType = StrToProgramType(plan.program_type, plan.selected_mode ?? "none")
        focus = plan.focus ?? null
        specificity = (programType != ProgramType.Continuous && programType !== ProgramType.Challenge) ? plan.specificity : null
        if (focus) updateFocusPower(focus as FocusTypes)
        planDate = plan.hasForecast ? LocalDate.atNoon(plan.date.substring(0, 10)) : fromUnixTime($trainingAdvice.target_event_timestamp)
        // During Pre-Plan Maintenance plan.start_date is in the future; the timeline starts when the plan was created.
        planCreationDate = LocalDate.toDate(((plan.is_pre_plan ? plan.created_at : plan.start_date) ?? plan.created_at).substring(0, 10))

        currentTLs = {
            xlss: advice.at_state.tl.ftp,
            xhss: advice.at_state.tl.hie,
            xpss: advice.at_state.tl.pp,
            xss: advice.at_state.tl.ftp + advice.at_state.tl.hie + advice.at_state.tl.pp,
        }
        currentSignature = {
            xlss: advice.signature.ftp,
            xhss: advice.signature.atc / 1000,
            xpss: advice.signature.pp,
            xss: 0,
        }
        const forecastDays: any[] = plan.forecastResults?.days ?? null

        const HundredDaysMinScrollBack = addDays(planDate, -130)
        const continuousScrollBackDate = addDays(dateNow, -7*6)
        let ts_startDate = planCreationDate // Always starts at plan creation unless Continuous
        if (programType === ProgramType.Continuous) ts_startDate = continuousScrollBackDate
        

        sliderStartDateIdx = differenceInDays(ts_startDate, endOfDay(dateNow))
        if (programType === ProgramType.BaseBuildPeak) sliderStartDateIdx = differenceInDays(HundredDaysMinScrollBack, endOfDay(dateNow))

        // NOTE: XPMC state will be retrived from ts_startDate to planDate
        // BBP plans are weird here - XPMC retrieval is the same, but he slider min will be a full 120 days ...
        // ... back no matter what (use onInput to restrict the selection)

        planStartIdx = differenceInDays(planCreationDate, endOfDay(dateNow))

        getTLFitSigFromHistory(addDays(ts_startDate, -1), addDays(planDate, 1), ($focusPowerDuration ?? 5)*60).then(onHistoryLoaded)

        if (plan.hasForecast && forecastDays) { // XFAI plan
            const day0 = forecastDays[0]
            const dayFinal = forecastDays[forecastDays.length - 1]
            startingSignature = {
                xss: 0,
                xlss: day0.ftp,
                xpss: day0.pp,
                xhss: day0.hie / 1000,
            }
            finalSignature = {
                xss: 0,
                xlss: dayFinal.ftp,
                xpss: dayFinal.pp,
                xhss: dayFinal.hie / 1000,
            }
            startingTLs = {
                xss: day0.tls.l + day0.tls.h + day0.tls.p,
                xlss: day0.tls.l,
                xpss: day0.tls.p,
                xhss: day0.tls.h,
            }
            finalTLs = {
                xss: dayFinal.tls.l + dayFinal.tls.h + dayFinal.tls.p,
                xlss: dayFinal.tls.l,
                xpss: dayFinal.tls.p,
                xhss: dayFinal.tls.h,
            }
            startingFocusPower = day0.fp
            finalFocusPower = dayFinal.fp

            const eventDay = forecastDays.find(d => d.state.includes("event_A"))
            if (!eventDay) throw new Error('Event day or today day not found in forecast')
            const eventDate = fromUnixTime(eventDay.t)
            showExpectedFor_dayIndexMax = differenceInDays(eventDate, startOfDay(dateNow))

            updateExpectedDay() // Uses showExpectedFor_dayIndex

            if (programType === ProgramType.Event) {
                eventPlan = createEventPlan(plan, advice, params, $cal_settings)
                eventXSS = {
                    xss: eventDay.xss,
                    xlss: eventDay.xlss,
                    xpss: eventDay.xpss,
                    xhss: eventDay.xhss,
                }
            } else if (programType === ProgramType.Race) {
                const target_event = plan.target_event
                racePlan = createRacePlan(plan, advice, params, $cal_settings)
                eventXSS = {
                    xss: target_event.xss,
                    xlss: target_event.xlss,
                    xpss: target_event.xpss,
                    xhss: target_event.xhss,
                }
                sfd = target_event.sfd
                sp = target_event.sp
                const currentSignatureReal: Signature = advice.signature
                raceAnalytics = new AnalyticsController(onUpdateSummary)

                logoURL = plan.event_logo_url ?? null
            }

            if (forecastDays.length > 0) {
                const result = await getForecastResultsForChart(plan.forecastResults, plan.settings, $cal_settings, $trainingMedians)
                lastInputs = plan.forecastResults.inputs
                chartData = result.forecastResults['days']
            }
        } else { // XATA plan
            programAchievability.validateRampRates($trainingAdvice.ir, currentTLs)
            irText = params.training_gradients.find(ir => ir.value === advice.training_gradient)?.text
            showExpectedFor_dayIndexMax = differenceInDays(planDate, startOfDay(dateNow))
        }

        focusDuration = programType === ProgramType.Race ? (round(sfd/60, 1)).toString() : $focusPowerDuration.toString()
   }

    const updateExpectedDay = () => {
        if (showExpectedFor_dayIndex < planStartIdx && programType !== ProgramType.Continuous) {
            // NOTE: BBP is allowed to have invalid values to maintain slider visuals, correct the value.
            // Other plans can also get here (e.g. plan start_date is later than today), clamp rather than throw
            // so that the rest of the page still renders.
            if (programType !== ProgramType.BaseBuildPeak) console.warn('Slider value before plan start, using plan start', {showExpectedFor_dayIndex, planStartIdx})
            showExpectedFor_dayIndex = planStartIdx
        }
        // NOTE: Only Continuous programs will retrieve 6-weeks back no matter the plan start date

        willShowCurrentValues = !$trainingPlan?.hasForecast || showExpectedFor_dayIndex >= 0
        curSliderDate = addDays(startOfDay(dateNow), showExpectedFor_dayIndex)
        xpmcProjDate = curSliderDate
        
        if (willShowCurrentValues) {
            // In this branch if XATA or future
            xpmcDayTypeLabel = 'Today'
            xpmcProjDate = dateNow
        } else if (showExpectedFor_dayIndex >= 0) {
            // NOTE: (Don't think this branch ever runs anymore) In this branch if XFAI and future
            xpmcDayTypeLabel = 'Projected'
        } else if (showExpectedFor_dayIndex >= planStartIdx) {
            // In this branch if XFAI and past
            xpmcDayTypeLabel = 'Completed'
        } else {
            // NOTE: I don't think this branch ever runs anymore
            xpmcDayTypeLabel = 'Before Start'
        }

        const isPrePlanDay = $trainingPlan?.is_pre_plan && $trainingPlan.forecastResults?.days?.length > 0 && curSliderDate < startOfDay(fromUnixTime($trainingPlan.forecastResults.days[0].t))
        if (isPrePlanDay) {
            // Before the plan starts: training loads decay from real activities
            forecastedDayTypeLabel = 'Pre-Plan'
        } else if (willShowCurrentValues && $trainingPlan?.hasForecast) {
            // In this branch if XFAI and future
            forecastedDayTypeLabel = 'Forecasted'
        } else if (showExpectedFor_dayIndex >= 0) {
            // In this branch if XATA and future
            forecastedDayTypeLabel = 'Projected'
        } else if ($trainingPlan?.hasForecast) {
            // XFAI past
            forecastedDayTypeLabel = 'Expected'
        } else {
            // XATA past
            forecastedDayTypeLabel = 'Completed'
        }

        const searchForDate = addDays(startOfDay(dateNow), showExpectedFor_dayIndex)

        if (weeklyStats) {
            let weeklyStatKeys = Object.keys(weeklyStats)
            for (let i = 0; i < weeklyStatKeys.length; i++) {
                const weekStartDate = LocalDate.toDate(weeklyStatKeys[i])
                if (weekStartDate > searchForDate) break

                thisWeekStats = weeklyStats[weeklyStatKeys[i]]
            }
        }

        if (!xpmcProj_isReady) {
            xpmcProjSignature = finalSignature
            xpmcProjTLs = finalTLs
            xpmcProjFocusPower = finalFocusPower
            return
        }

        const xpmcProjectedDay = xpmcProjectedData.find(data => isSameDay(data.date, searchForDate))
        if (!xpmcProjectedDay) console.error('XPMC projected day not found, about to throw.', {showExpectedFor_dayIndex, searchForDate, historical_data: xpmcProjectedData})
        xpmcProjSignature = {
            xss: 0,
            xlss: xpmcProjectedDay.signature.ftp,
            xpss: xpmcProjectedDay.signature.pp,
            xhss: xpmcProjectedDay.signature.atc / 1000,
        }
        xpmcProjTLs = {
            xss: xpmcProjectedDay.tl.ftp + xpmcProjectedDay.tl.hie + xpmcProjectedDay.tl.pp,
            xlss: xpmcProjectedDay.tl.ftp,
            xpss: xpmcProjectedDay.tl.pp,
            xhss: xpmcProjectedDay.tl.hie,
        }
        xpmcProjFocusPower = xpmcProjectedDay.fp

        if (!willShowCurrentValues) {
            const forecastDays = $trainingPlan.forecastResults.days

            const forecastDay = forecastDays.find(day => isSameDay(fromUnixTime(day.t), searchForDate))

            if (forecastDay) {
                forecastSignature = {
                    xss: 0,
                    xlss: forecastDay.ftp,
                    xpss: forecastDay.pp,
                    xhss: forecastDay.hie / 1000,
                }
                forecastTLs = {
                    xss: forecastDay.tls.l + forecastDay.tls.h + forecastDay.tls.p,
                    xlss: forecastDay.tls.l,
                    xpss: forecastDay.tls.p,
                    xhss: forecastDay.tls.h,
                }
                forecastFocusPower = forecastDay.fp
            } else {
                console.warn('Forecast day not found. Using XPMC day', {searchForDate, forecastDays})
                forecastSignature = xpmcProjSignature
                forecastTLs = xpmcProjTLs
                forecastFocusPower = xpmcProjFocusPower
            }
        } else {
            // Forecast variables are reused
            forecastSignature = xpmcProjSignature
            forecastTLs = xpmcProjTLs
            forecastFocusPower = xpmcProjFocusPower
            xpmcProjSignature = currentSignature
            xpmcProjTLs = currentTLs
            xpmcProjFocusPower = $currentFocusPower
        }
    }

    const onUpdateSummary = (summaryUpdates) => {
        summary = {
            ...summary,
            ...summaryUpdates
        }
    }
    const afterZoom = (startIndex: number, endIndex: number, data: [any]) => {
        raceAnalytics.processMpaDataSelection(data, startIndex, endIndex, xssDetailsToSignature(finalSignature))
    }

    $: if ($trainingPlan && $trainingAdvice && $userParams) onPlanUpdated($trainingPlan, $trainingAdvice, $userParams)

    $: xpmcProj_isReady, showExpectedFor_dayIndex, updateExpectedDay() // Reacts to showExpectedFor_dayIndex
</script>

<ResponsiveHalfView {hidden} >
    <svelte:fragment slot='left'>

        <div class="flex flex-col gap-6 border border-solid rounded shadow-lg shadow-xerteal-600/10 border-xerteal-600/10 sm:px-8 px-5 py-5 order-1 program-info-cq">

            {#if $trainingPlan?.is_pre_plan && $trainingPlan.forecastResults?.days?.length}
                <PrePlanBanner startDate={startOfDay(fromUnixTime($trainingPlan.forecastResults.days[0].t))} />
            {/if}

            <!-- A forecast plan in progress (not the pre-plan maintenance period, not Continuous): the plan status report -->
            {#if $trainingPlan?.hasForecast && !$trainingPlan?.is_pre_plan && [ProgramType.Goal, ProgramType.Event, ProgramType.Race].includes(programType)}
                <BreakthroughProposal />
                <PlanStatusBanner />
            {/if}

            <!-- Program Title -->
            <div class="flex gap-6 items-center justify-center flex-wrap">
                <ProgramNameIcon {programType} />
                <div class="grid grid-cols-[max-content,auto] gap-x-3 gap-y-1 items-baseline">
                    
                    {#if programType !== ProgramType.Continuous}
                    <i class="fa-solid {programTypeIcon[programType]} justify-self-center text-xerteal-700"/>
                    <span class=""> {format(planDate, 'do MMMM yyyy')}</span>
                    {/if}
                       
                    <i class="fa-regular fa-badge justify-self-center text-xerteal-700"/>
                    <span class="">{specificity ?? ""} {focus}</span>

                    <i class="fa-regular fa-badge justify-self-center text-xerteal-700"/>
                    <span class="">
                        {#if programType !== ProgramType.Race}
                            {focusDuration} Minute Power{specificity ? ` with ${specificityValues[specificity]*100}% Specificity` : ""}
                        {:else}
                            {focusDuration} Minute Power{specificity ? ` with ${round(sp*100)}% Specificity` : ""}
                        {/if}  
                    </span>

                    
                </div>

                {#if !xpmcProj_isReady}
                    <i class="fa-regular fa-spinner fa-spin h-min"/>
                {/if}
            </div>

            <PredictiveModeBadge />

            <div class="h-px w-auto bg-black/10"/>

            {#if [ProgramType.Challenge, ProgramType.Continuous, ProgramType.BaseBuildPeak].includes(programType)}
                <div class="flex flex-col gap-4">
                    <span class="text-base">
                        <span class="inline-flex gap-1 items-baseline"><i class="fa-regular fa-chart-line mr-1"/> Improvement Rate - </span>
                        <span class="font-bold">{irText}</span>
                    </span>
                    <span class="text-slate-500">{improvementRateDescriptions[$trainingAdvice.ir]}</span>
                    
                    {#if $overallAchievability != 'fine'}
                        <ProgramAchievabilityMessage status={$overallAchievability} statusMessage={$statusMessage}/>
                    {/if}
                </div>       
            {/if}

            {#if programType === ProgramType.Goal}
                <FocusPowerIncrease  
                    planSaved 
                    focusPowerDuration={$focusPowerDuration} currentFocusPower={$currentFocusPower} 
                    projectedFocusPower={finalFocusPower} date={planDate}
                />
            {:else if (programType === ProgramType.Event && eventPlan)}
                <div class="flex flex-col gap-5">
                    <div class="font-bold text-lg">{eventPlan.name}</div>
                    <div class="grid event-info-box justify-items-start items-start">
                        <span class="text-slate-500">Terrain</span>
                        <span class="text-slate-500">Duration</span>
                        <span class="text-base font-medium">{eventPlan.terrain} {eventPlan.type}</span>
                        <span class="text-base font-medium">{durationToHoursMinutes(eventPlan.duration)}</span>
                        <span class="text-slate-500 mt-4">XSS</span>
                        <span class="text-slate-500 mt-4">Readiness</span>
                        <XfaiXss class="px-3 py-3 border-dashed border-gray-500 border-[1px] rounded-lg" xss={eventXSS} />
                        <EventReadiness taper={eventPlan.settings.taper} />
                    </div>
                </div>
            {:else if (programType === ProgramType.Race && racePlan)} 
                
                {@const raceDayWeight = racePlan.userTargetWeight}
                {@const currentWeight = getDisplayWeight($userParams.current_weight, $userParams.weight_unit)}
                {@const diffWeight = currentWeight - raceDayWeight}

                <div class="grid gap-x-6 gap-y-2 justify-items-start race-info-box mb-3">
                    {#if logoURL}
                        <img class="max-h-[120px] object-contain row-span-3 rounded-md" src={logoURL} alt='race-logo' />
                    {/if}
                    <div class="col-start-2 font-bold text-lg self-end h-full flex items-center race-plan-name">
                        {racePlan.name}
                    </div>
                    
                    <div class="col-start-2 grid grid-cols-[auto,auto] gap-2 items-baseline race-plan-weights">
                        <span class="text-slate-500">Current Weight:</span>
                        <span class="text-base">{currentWeight.toFixed(0)} {$userParams.weight_unit}{` `}
                            <span class="text-slate-600">{`(${Intl.NumberFormat("en-US", {signDisplay:"always"}).format(Math.round(diffWeight))})`}</span>
                        </span>
                        <span class="text-slate-500"> Race Day Weight:</span>
                        <span class="text-base">{raceDayWeight.toFixed(0)} {$userParams.weight_unit}</span>
                    </div>
                </div>
            {/if}
            
            <div class="flex flex-row flex-nowrap gap-3">
                {#if isGoogleMapsLoaded && programType === ProgramType.Race && racePlan}
                    <Button variant="outline" on:click={showMapModal}>Show Details</Button>

                    <RaceMapModal
                        bind:show={showMapModal} bind:chartAvgDataOverSeconds 
                        onSliderInput={() => debounceRefreshChartMpaData.debounce(refreshChartMpaData)}
                        {mpaData} {summary} {afterZoom}
                        mapPolyline={raceRecords?.polyline} mapUrl={`/activities/${racePlan.activityPath}/map`}
                    />
                {/if}

                <div class="flex flex-wrap items-center justify-end gap-3 ml-auto">
                    {#if ownPlan}<AdaptForecastNeeded />{/if}
                    <Button variant="outline" class="gap-2" on:click={editPlanCallback}>
                        <i class="fa-regular fa-edit"/>
                        Modify Plan
                    </Button>
                </div>
            </div>
        </div>

        {#if programType === ProgramType.BaseBuildPeak && thisWeekStats}
    
            {@const sliderDate = addDays(new Date(), showExpectedFor_dayIndex)}
            {@const weekStart = startOfWeek(sliderDate)}
            {@const weekEnd = endOfWeek(sliderDate)}
            <div class="col-span-2 border border-solid rounded shadow-inner shadow-black/10 border-black/10 sm:px-8 px-5 py-5 order-3">
                <h4 class="font-bold mt-0 mb-5 text-lg">
                    <span class="font-normal">{`${forecastedDayTypeLabel} Week`}</span> {`${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')}`}
                </h4>
                <div class="flex flex-row flex-nowrap justify-around text-center mb-5">
                    <div>
                        <div class="font-bold text-xl">
                            {thisWeekStats.weekly_hours.toFixed(1)} hours
                        </div>
                        <div class="text-slate-500 text-sm">
                            Needed this week
                        </div>
                    </div>
                    <div>
                        <div class="font-bold text-xl">
                            {thisWeekStats.phase}
                        </div>
                        <div class="text-slate-500 text-sm">
                            Program Phase
                        </div>
                    </div>
                </div>
                <div class="text-center">
                    <span class="font-bold">{focus}</span> - {$focusPowerDuration} minute power
                </div>
            </div>
        {/if}
        
        <!-- Training Load, Status -->
        {#if startingTLs && currentTLs && xpmcProjTLs && finalTLs && forecastTLs}
            {@const TLmax = Math.max(xpmcProjTLs.xss, finalTLs.xss, (tlMax?.xss ?? 0))}
            {@const pcntXpmcTL = xpmcProjTLs.xss / TLmax * 100}
            {@const pcntForecastTL = forecastTLs.xss / TLmax * 100}
            {@const isBehind = roundTo1Decimal(xpmcProjTLs.xss) < roundTo1Decimal(forecastTLs.xss)}
            {@const expectedColor = isBehind ? '#dc2626' : '#16a34a'}

            <div in:fade={{duration:1000}} class="order-3">
                
                <div class="border border-solid rounded shadow-inner shadow-black/10 border-black/10 sm:px-8 px-5 py-5">
                <div class="flex justify-between flex-wrap gap-2">
                    <span class="text-slate-500 col-span-3 text-lg">Training Loads - <strong>{xpmcDayTypeLabel}</strong></span>
                    <div class="row-span-3 col-start-2 self-start grid grid-cols-[min-content,auto,auto] items-center gap-x-4 gap-y-2 border rounded shadow-black/10 border-black/10 px-4 py-2">
                        <CurrentMarker class="text-blue-400 w-3 justify-self-center"/>
                        <span>{xpmcDayTypeLabel}</span>
                        <span class="border rounded-full border-slate-400 text-slate-500 px-2">{format(xpmcProjDate, "dd MMM ''yy")}</span>
                        <TargetMarker class="text-blue-400 w-4 justify-self-center" strokeWidth={10}/>
                        <span>{forecastedDayTypeLabel}</span>
                        <span class="border rounded-full border-slate-400 text-slate-500 px-2">{format(curSliderDate, "dd MMM ''yy")}</span>
                    </div>
                </div>

                <div class="w-max mx-auto py-2 px-3 bg-[#e6e6e6] rounded-lg font-bold mt-6">
                    Training Status
                    {#if xpmcProjTLs}
                        <!-- be careful with this, only ts_rating is supplied, so other options won't work -->
                        <TrainingStatus trainingAdvice={{training_status: { ts_rating: calcTsRating(xpmcProjTLs.xss) }}} expected_tsRating={forecastTLs ? calcTsRating(forecastTLs.xss) : null} enableFade={false} showTooltip={false} showText={false} hideLeftLabel={true} showColour={false} />
                    {:else}
                        <i class="fa-regular fa-spinner fa-spin ml-4"/>
                    {/if}
                </div>

                <div class="flex flex-row max-w-[455px] mx-auto mb-[40px] mt-6">
                    <!-- Total TL label -->
                    <span class="font-bold px-3 py-2 rounded-lg h-full bg-[#e6e6e6]">Training Load | {xpmcProjTLs.xss.toFixed(0)}</span>
                    <!-- Actual Bar -->
                    <div 
                        class="ml-3 flex-1 rounded-lg relative"
                        style={`background: ${getLHPGradient(xpmcProjTLs, TLmax, 100, 'transparent')}, ${getLHPGradient(forecastTLs, TLmax, 0.4)}`}
                    >
                        <!-- Current/Target Markers -->
                        <TargetMarker class="text-blue-400 absolute top-[-10px] h-[18px]" style="transform: translate(-50%, -50%); left: {pcntForecastTL}%" strokeWidth={10} />
                        <CurrentMarker class="text-blue-400 absolute top-[-10px] h-[12px]" style="transform: translate(-50%, -50%); left: {pcntXpmcTL}%" />
                        <!-- Bar's bounds & expected total -->
                        <span class="absolute top-[110%] left-0 font-bold">0</span>
                        <span class="absolute top-[110%] left-1/2 flex flex-row flex-nowrap items-center w-min" style="transform: translateX(-50%); color: {expectedColor}">
                            <span class="hidden sm:block">
                                {forecastedDayTypeLabel}
                            </span>
                            <span class="sm:hidden block h-[18px]">
                                <TargetMarker class="text-blue-400 h-[18px]" strokeWidth={10}/>
                            </span>
                            {` `}
                            <strong class="ml-1 block">{forecastTLs.xss.toFixed(0)}</strong>
                        </span>
                        <span class="absolute top-[110%] right-0">
                            <span class="hidden sm:inline">{`Final `}</span>
                            <strong>{finalTLs.xss.toFixed(0)}</strong>
                        </span>
                    </div>
                </div>

                <div class="flex flex-row flex-wrap gap-1 justify-evenly mb-6">
                    <div class="meter-container">
                        <PlanMeter
                            label="Low"
                            value={xpmcProjTLs.xlss}
                            max={tlMax?.xlss ?? finalTLs.xlss}
                            min={tlMin?.xlss ?? 0}
                            expectedMarker={{label: 'Expected', value: forecastTLs.xlss}}
                            color="var(--color-xlss)"
                            {forecastedDayTypeLabel}
                            showDiff={!willShowCurrentValues}
                        />
                    </div>
                    <div class="meter-container">
                        <PlanMeter
                            label="High"
                            value={xpmcProjTLs.xhss}
                            max={tlMax?.xhss ?? finalTLs.xhss}
                            min={tlMin?.xhss ?? 0}
                            expectedMarker={{label: 'Expected', value: forecastTLs.xhss}}
                            color="var(--color-xhss)"
                            {forecastedDayTypeLabel}
                            showDiff={!willShowCurrentValues}
                            precision={1}
                        />
                    </div>
                    <div class="meter-container">
                        <PlanMeter
                            label="Peak"
                            value={xpmcProjTLs.xpss}
                            max={tlMax?.xpss ?? finalTLs.xpss}
                            min={tlMin?.xpss ?? 0}
                            expectedMarker={{label: 'Expected', value: forecastTLs.xpss}}
                            color="var(--color-xpss)"
                            {forecastedDayTypeLabel}
                            showDiff={!willShowCurrentValues}
                            precision={1}
                        />
                    </div>
                </div>

                <Accordion class="py-2 px-4 border border-solid border-black/10 rounded">
                    <div slot="trigger" class="text-slate-600 text-left">Table View</div>
                    <div class="mt-2" slot="content">
                        <PlanFitSigTLTable
                            start={startingTLs}
                            xpmcProj={xpmcProjTLs}
                            required={forecastTLs}
                            final={finalTLs}
                            lowLabel="Low"
                            highLabel="High"
                            peakLabel="Peak"
                            totalLabel="Total"
                            {xpmcDayTypeLabel}
                            {forecastedDayTypeLabel}
                        />
                    </div>
                </Accordion>

            </div>
            </div>
        {:else}
            <Skeleton class="w-full h-[500px] order-3"/>
        {/if}   
        
        {#if chartData}
            <div class="flex flex-col gap-6 border border-solid rounded shadow-inner shadow-black/10 border-black/10 sm:px-8 px-5 py-5 order-4">
                <span class="text-slate-500 col-span-3 text-lg">Forecast Chart</span>
                <!-- <button on:click={adaptForecastModal.show} class='group/crysball flex justify-center items-center calendar-button mx-auto' >
                    <span class='relative px-[2px] pt-1 mr-1'>
                        <XfaiBall class="h-5" type="hover"/>
                        <div class="red-dot" hidden={isForecastUpToDate} />
                    </span>
                    <span class='extra-text2'>Adapt Forecast</span>
                </button> -->
                
                <Am5Loader>
                    <ProgressionChart id="progression_chart_programpage" athletes={$userParams.athletes} data={chartData} />
                </Am5Loader>
            </div>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="right">

        <!-- Slider Tile -->
        <div class="slider-container grid grid-cols-[auto,auto] gap-x-6 gap-y-6 border border-solid rounded shadow-lg shadow-black/10 border-black/10 sm:px-8 px-5 py-5 order-2">           

            <!-- Show Fitness -->
            <span class="text-base self-end">
                Comparing your
                {#key xpmcDayTypeLabel}
                <span class="slider-language inline-flex align-bottom gap-1 font-bold text-lg mx-1"><CurrentMarker class="text-blue-400 h-[12px] self-center"/> {xpmcDayTypeLabel}</span>
                {/key}
                fitness values on the
                <span  class="font-bold text-lg mx-1">{format(xpmcProjDate, 'do MMM')}</span>
                with your 
                {#key forecastedDayTypeLabel}
                <span class="slider-language inline-flex align-bottom gap-1 font-bold text-lg mx-1"><TargetMarker class="text-blue-400 h-[18px] self-center" strokeWidth={10}/> {forecastedDayTypeLabel}</span>
                {/key}
                fitness values on the
                <span class="font-bold text-lg mx-1">{format(curSliderDate, 'do MMM')}</span>                
            </span>

            <!-- Legend -->
            <div class="row-span-2 col-start-2 self-start grid legend-grid items-center gap-x-4 gap-y-2 border rounded shadow-black/10 border-black/10 px-4 py-2">
                <CurrentMarker class="text-blue-400 h-[12px] justify-self-center"/>
                <span class="md:text-left text-right legend-label">{xpmcDayTypeLabel}</span>
                <span class="hide-date border rounded-full border-slate-400 text-slate-500 px-2">{format(xpmcProjDate, "dd MMM ''yy")}</span>

                <TargetMarker class="text-blue-400 h-[18px] justify-self-center" strokeWidth={10}/>
                <span class="md:text-left text-right legend-label">{forecastedDayTypeLabel}</span>
                <span class="hide-date border rounded-full border-slate-400 text-slate-500 px-2">{format(curSliderDate, "dd MMM ''yy")}</span>

                <DottedLineMarker class="text-black h-5 justify-self-center" strokeWidth={5}/>
                <span class="md:text-left text-right legend-label">Today</span>
                <span class="hide-date border rounded-full border-slate-400 text-slate-500 px-2">{format(new Date(), "dd MMM ''yy")}</span>
                
                {#if programType !== ProgramType.Continuous}
                <i class="fa-regular fa-calendar text-xerteal-700 justify-self-center"/>
                <span class="md:text-left text-right legend-label">Start</span>
                <span class="hide-date border rounded-full border-slate-400 text-slate-500 px-2">{format(planCreationDate, "dd MMM ''yy")}</span>
                {/if}

                <i class="fa-solid {programTypeIcon[programType]} text-xerteal-700 justify-self-center"/>
                <span class="md:text-left text-right legend-label">{programType !== ProgramType.Continuous ? 'End' : '+6 Weeks'}</span>
                <span class="hide-date border rounded-full border-slate-400 text-slate-500 px-2">{format(planDate, "dd MMM ''yy")}</span>

            </div>

            <!-- Show Today/Start/End Buttons -->
            <div class="flex flex-wrap self-start gap-2">
                <button
                    class="btn-xfai-sm text-sm bg-transparent hover:bg-xerteal-50 text-xerteal-700 border-xerteal-700 border gap-2"
                    on:click={() => showExpectedFor_dayIndex = 0}
                >
                    <DottedLineMarker class="text-black h-5 justify-self-center" strokeWidth={5}/>
                    <span>Today</span>
                </button>
                
                
                {#if programType !== ProgramType.Continuous}
                    <button 
                            class="btn-xfai-sm text-sm bg-transparent hover:bg-xerteal-50 text-xerteal-700 border-xerteal-700 border gap-2"
                            on:click={() => showExpectedFor_dayIndex = planStartIdx}
                        >
                        <i class="fa-regular fa-calendar text-xerteal-700 justify-self-center"/>
                        <span>Start</span>
                    </button>
                {/if}

                <button 
                        class="btn-xfai-sm text-sm bg-transparent hover:bg-xerteal-50 text-xerteal-700 border-xerteal-700 border gap-2"
                        on:click={() => showExpectedFor_dayIndex = showExpectedFor_dayIndexMax}
                    >
                    <i class="fa-solid {programTypeIcon[programType]} text-xerteal-700 justify-self-center"/>
                    <span>{programType !== ProgramType.Continuous ? 'End' : '+6 Weeks'}</span>
                </button>
                
            </div>
 
            <!-- Slider -->
            <!-- I tried using on:input here, but looks like the data doesn't flow correctly / there's a race somewhere -->
            <div class="relative col-span-2 my-3">
                {#if true} <!-- I just need this to make consts here -->
                    {@const sliderRange = showExpectedFor_dayIndexMax - sliderStartDateIdx}
                    {@const todayPcnt = (0 - sliderStartDateIdx) / sliderRange * 100}
                    {@const planStartPcnt = (planStartIdx - sliderStartDateIdx) / sliderRange * 100}
                    {@const sliderPcnt = (showExpectedFor_dayIndex - sliderStartDateIdx) / sliderRange * 100}

                    <span class="absolute z-10 shift-xy bottom-1/2 h-full pointer-events-none" style="left: {todayPcnt}%">
                        <DottedLineMarker class="text-black h-full"/>
                    </span>

                    {#if programType !== ProgramType.Continuous}
                    <span class="absolute shift-x z-20 -bottom-4 pointer-events-none" style="left: {planStartPcnt}%">
                        <i class="fa-regular fa-calendar text-xerteal-700"/>
                    </span>
                    {/if}
                    <span class="absolute shift-x z-20 -bottom-4 pointer-events-none left-full">
                        <i class="fa-solid {programTypeIcon[programType]} text-xerteal-700"/>
                    </span>
                    
                    <TargetMarker 
                        class="text-blue-400 h-[18px] absolute z-10 bottom-full pointer-events-none"
                        style="left: calc({sliderPcnt}%); transform: translate(-50%, 50%);"
                        strokeWidth={10}
                    />

                    <CurrentMarker
                        class="text-blue-400 h-[12px] absolute z-10 bottom-full pointer-events-none"
                        style="left: calc({willShowCurrentValues ? todayPcnt : sliderPcnt}%); transform: translate(-50%, 50%);"
                    />
                {/if}

                <CurrentTpDateSlider {programType} {rampRateStats} bind:value={showExpectedFor_dayIndex} min={sliderStartDateIdx} max={showExpectedFor_dayIndexMax} disabled={!xpmcProj_isReady} />
            </div>
        </div>

        {#if startingSignature && forecastSignature && xpmcProjSignature && finalSignature && startingFocusPower && forecastFocusPower && xpmcProjFocusPower && finalFocusPower}
            <div in:fade={{duration:1000}} class="order-3">
            <div class="flex flex-col gap-6 border border-solid rounded shadow-inner shadow-black/10 border-black/10 sm:px-8 px-5 py-5">

                <div class="flex justify-between flex-wrap gap-2">
                    <span class="text-slate-500 col-span-3 text-lg">Fitness Signature - <strong>{xpmcDayTypeLabel}</strong></span>
                    <div class="row-span-3 col-start-2 self-start grid grid-cols-[min-content,auto,auto] items-center gap-x-4 gap-y-2 border rounded shadow-black/10 border-black/10 px-4 py-2">
                        <CurrentMarker class="text-blue-400 w-3 justify-self-center"/>
                        <span>{xpmcDayTypeLabel}</span>
                        <span class="border rounded-full border-slate-400 text-slate-500 px-2">{format(xpmcProjDate, "dd MMM ''yy")}</span>
                        <TargetMarker class="text-blue-400 w-4 justify-self-center" strokeWidth={10}/>
                        <span>{forecastedDayTypeLabel}</span>
                        <span class="border rounded-full border-slate-400 text-slate-500 px-2">{format(curSliderDate, "dd MMM ''yy")}</span>
                    </div>
                </div>

                <div class='grid grid-cols-2 grid-flow-row gap-1 justify-evenly'>
                    <div class='meter-container'>
                        <PlanMeter
                            label="TP"
                            min={sigMin?.xlss}
                            value={xpmcProjSignature.xlss}
                            max={sigMax?.xlss ?? finalSignature.xlss}
                            expectedMarker={{label: 'Expected', value: forecastSignature.xlss}}
                            color="var(--color-xlss)"
                            {forecastedDayTypeLabel}
                            showDiff={!willShowCurrentValues}
                            units="W"
                        />
                    </div>
                    <div class='meter-container'>
                        <PlanMeter
                            label="HIE"
                            min={sigMin?.xhss}
                            value={xpmcProjSignature.xhss}
                            max={sigMax?.xhss ?? finalSignature.xhss}
                            expectedMarker={{label: 'Expected', value: forecastSignature.xhss}}
                            color="var(--color-xhss)"
                            {forecastedDayTypeLabel}
                            showDiff={!willShowCurrentValues}
                            precision={1}
                            units="kJ"
                        />
                    </div>
                    <div class='meter-container'>
                        <PlanMeter
                            label="PP"
                            min={sigMin?.xpss}
                            value={xpmcProjSignature.xpss}
                            max={sigMax?.xpss ?? finalSignature.xpss}
                            expectedMarker={{label: 'Expected', value: forecastSignature.xpss}}
                            color="var(--color-xpss)"
                            {forecastedDayTypeLabel}
                            showDiff={!willShowCurrentValues}
                            units="W"
                        />
                    </div>
                    <div class='meter-container'>
                        <PlanMeter
                            label={`${focusDuration} min`}
                            min={fpLimits?.min}
                            value={xpmcProjFocusPower}
                            max={fpLimits?.max}
                            expectedMarker={{label: 'Expected', value: forecastFocusPower}}
                            color="var(--color-xert-teal)"
                            {forecastedDayTypeLabel}
                            showDiff={!willShowCurrentValues}
                            units="W"
                        />
                    </div>
                </div>

                <Accordion class="py-2 px-4 border border-solid border-black/10 rounded">
                    <div slot="trigger" class="text-slate-600 text-left">Table View</div>
                    <div class="mt-2" slot="content">
                        <PlanFitSigTLTable
                            start={{...startingSignature, focusPower: startingFocusPower}}
                            xpmcProj={{...xpmcProjSignature, focusPower: xpmcProjFocusPower}}
                            required={{...forecastSignature, focusPower: forecastFocusPower}}
                            final={{...finalSignature, focusPower: finalFocusPower}}
                            lowLabel="TP (W)"
                            highLabel="HIE (kJ)"
                            peakLabel="PP (W)"
                            focusPowerLabel={`${focusDuration} min (W)`}
                            {xpmcDayTypeLabel}
                            {forecastedDayTypeLabel}
                        />
                    </div>
                </Accordion>
                
            </div>
            </div>
        {:else}
            <Skeleton class="w-full h-[500px] order-3"/>
        {/if}
    </svelte:fragment>
</ResponsiveHalfView>


<style lang="scss">
    
    .half-flex {
        flex: 1;
        display: flex;
        flex-direction: column;
        row-gap: 1rem;
        flex-wrap: nowrap;
        min-width: 40%;
    }
    .meter-container {
        flex: 1 1 0%;
        max-height: 152px;
    }

    .slider-label-btn {
        border-radius: 0.5rem;
        border: 0px;
        padding: 3px 5px;
        transition: background-color 0.2s ease-in;
        position: absolute;
        top: 68px;
        background: transparent;
        &:hover {
            background-color: #d6d6d6 !important;
        }
    }
    .shift-xy {
        transform: translate(-50%, 50%);
    }
    .shift-x {
        transform: translateX(-50%);
    }
    .calendar-button {
        background-color: #fff;
        border: 1px solid #3D3D3D;
        border-radius: 3px;
        font-size: 16px;
        color: #3D3D3D;
        padding: 4px 8px;
        &:focus-visible {
            background-color: #e6e6e6;
        }

        // Only do a hover effect if mouse cursor available,
        // otherwise the effect is sticky on mobile.
        @media (pointer: fine) {
            &:hover {
                background-color: #e6e6e6;
            }
        }
        &:active {
            background-color: #d4d4d4;
        }

    }
    .slider-language{
        transition: all;
        animation:  scale 1s ease-in-out 0s 1;
    }

    @keyframes scale {
        0% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.1);
        }
        100% {
            transform: scale(1);
        }
    }

    .slider-container {
        container-type: inline-size;

        .legend-grid {
            grid-template-columns: min-content auto auto;
        }

        @container (width < 530px) {
            .legend-grid {
                grid-template-columns: min-content auto;
            }
            .legend-label {
                text-align: left !important;
            }
            .hide-date {
                display: none;
            }
        }
    }

    .program-info-cq {
        container-type: inline-size;

        .race-info-box {
            grid-template-columns: min-content auto;
        }
        .event-info-box {
            grid-template-columns: 1fr 1fr;
        }
        .race-plan-name {
            overflow-wrap: anywhere;
        }
        @container (width < 450px) {
            .race-plan-name, .race-plan-weights {
                grid-column-start: 1 !important;
            }
            .race-plan-name {
                margin-top: 8px;
                align-self: auto !important;
            }
            .race-info-box {
                grid-template-columns: auto;
            }

            .event-info-box {
                grid-template-columns: min-content auto;
                gap: 16px 24px;
                grid-auto-flow: row;
                align-items: start;

                span:nth-child(3) {
                    grid-area: 1 / 2 / 1 / 2;
                }
                span:nth-child(4) {
                    grid-area: 2 / 2 / 2 / 2;
                }
                span:nth-child(5) {
                    margin-top: 0px !important;
                }
                span:nth-child(6) {
                    margin-top: 0px !important;
                }
                span:nth-child(7) {
                    grid-area: 3 / 2 / 3 / 2;
                }
                span:nth-child(8) {
                    grid-area: 4 / 2 / 4 / 2;
                }
            }
        }

    }
</style>